import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';
import { Text } from '@famly/mf_data-display_text';
import { Icon } from '@famly/mf_data-display_icon';
import { Button } from '@famly/mf_input_button';
import { createStack, Stack } from '@famly/mf_layout_stack';
import { Box } from '@famly/mf_layout_box';
import translate from 'signin-app/helpers/translate';
import RoutesMap from 'signin-app/routes/routes-map';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import PlatformHelper from 'web-app/helpers/platform-helper';
import LocalStorageService from 'web-app/services/local-storage';
import { useWhitelabelConfiguration } from 'signin-app/whitelabel/use-whitelabel-configuration';
import { WhitelabelLogo } from 'signin-app/whitelabel/whitelabel-logo';
import { Spinner } from '@famly/mf_feedback_spinner';
import * as Actions from './actions';
import * as Selectors from './selectors';
import Form from './form/form';
const CLOSED_ADD_TO_HOME_BANNER_COUNTER = 'HideAddToHomeBannerCounter';
const LoginContainer = styled.div `
    position: relative;
    height: 100vh;
`;
const ScreenCenter = styled(createStack({ left: '50%', position: 'absolute', top: '50%', zIndex: 2 })).attrs(() => ({
    direction: 'column',
    alignItems: 'center',
})) `
    transform: translate(-50%, -50%);
`;
const AddToHomeInstructions = ({ handleClose }) => {
    const buttonText = PlatformHelper.isRunningOnIOS()
        ? translate('addToHomeDescriptionShare')
        : translate('addToHomeDescriptionMore');
    const shareMoreIcon = PlatformHelper.isRunningOnIOS() ? 'upload' : 'more_vert';
    return (_jsxs(Stack, { flexDirection: "column", alignItems: "center", width: "320px", p: "16px", position: "absolute", top: "24px", right: "24px", backgroundColor: "p100", borderRadius: "16px", gap: "16px", zIndex: "1000", children: [_jsx(Box, { alignSelf: "flex-end", children: _jsx(Button, { icon: "close", "aria-label": i18next.t($ => $.close), variant: "transparent", onClick: handleClose }) }), _jsx(Text, { variant: "body", emphasized: true, maxWidth: "200px", textAlign: "center", children: translate('addToHomeTitle') }), _jsxs(Stack, { flexDirection: "column", alignItems: "center", children: [_jsx(Text, { variant: "body-small", mb: "8px", textAlign: "center", children: buttonText }), _jsx(Stack, { backgroundColor: "n0", width: "48px", height: "40px", justifyContent: "center", alignItems: "center", borderRadius: "10px", children: _jsx(Icon, { name: shareMoreIcon, color: "n300" }) })] }), _jsxs(Stack, { flexDirection: "column", alignItems: "center", children: [_jsx(Text, { variant: "body-small", mb: "8px", textAlign: "center", maxWidth: "250px", children: translate('addToHomeDescription2') }), _jsxs(Stack, { backgroundColor: "n0", height: "40px", width: "288px", borderRadius: "8px", alignItems: "center", justifyContent: "space-between", px: "12px", children: [_jsx(Text, { variant: "body-small", children: translate('addToHomeScreenAdd') }), PlatformHelper.isRunningOnIOS() ? _jsx(Icon, { name: "add", color: "n300" }) : null] })] })] }));
};
const Login = () => {
    const whitelabelConfiguration = useWhitelabelConfiguration();
    const isLoggedIn = useTypedSelector(state => Selectors.isLoggedIn(state));
    const isLoggingIn = useTypedSelector(state => Selectors.isLoggingIn(state));
    const isPinApp = useTypedSelector(state => Selectors.isPinApp(state));
    const isQrSignin = useTypedSelector(state => Selectors.isQrSignin(state));
    const dispatch = useDispatch();
    const [hideWidget, setHideWidget] = React.useState(() => LocalStorageService.getFromLocalStorage(CLOSED_ADD_TO_HOME_BANNER_COUNTER) > 2);
    const handleSubmit = React.useCallback((formData) => {
        dispatch(Actions.login.action(formData.password));
    }, [dispatch]);
    const shouldShowAddToHomeInstructions = React.useMemo(() => {
        const isStandaloneApp = window.matchMedia('(display-mode: standalone)').matches;
        const isTablet = PlatformHelper.isRunningOnTablet();
        if (hideWidget || isStandaloneApp) {
            return false;
        }
        return !hideWidget && !isStandaloneApp && isTablet;
    }, [hideWidget]);
    const handleClose = React.useCallback(() => {
        setHideWidget(true);
        const currentCount = LocalStorageService.getFromLocalStorage(CLOSED_ADD_TO_HOME_BANNER_COUNTER);
        LocalStorageService.saveToLocalStorage(CLOSED_ADD_TO_HOME_BANNER_COUNTER, !currentCount ? 1 : currentCount + 1);
    }, [setHideWidget]);
    if (isLoggedIn) {
        if (isQrSignin && !isPinApp) {
            return _jsx(Redirect, { to: RoutesMap.qr });
        }
        if (isPinApp) {
            return _jsx(Redirect, { to: RoutesMap.pin });
        }
        return _jsx(Redirect, { to: RoutesMap.overview });
    }
    if (isLoggingIn) {
        return (_jsx(Stack, { justifyContent: "center", width: "100%", children: _jsx(Spinner, { size: 32, color: "n0", "aria-label": i18next.t($ => $.react.components.loading.spinner.translations.json.loadingAriaLabel) }) }));
    }
    return (_jsx(_Fragment, { children: _jsxs(LoginContainer, { children: [shouldShowAddToHomeInstructions ? _jsx(AddToHomeInstructions, { handleClose: handleClose }) : null, _jsxs(ScreenCenter, { children: [_jsx(WhitelabelLogo, { url: whitelabelConfiguration.LoginLogo }), _jsx(Text, { variant: "h4", marginTop: 6, color: whitelabelConfiguration.LoginPagesFontColor, textAlign: "center", children: translate('logInHeadline', { whitelabelName: whitelabelConfiguration.ProductName }) }), _jsx(Text, { variant: "body", marginTop: 1, color: whitelabelConfiguration.LoginPagesFontColor, textAlign: "center", children: translate('loginDescription', { whitelabelName: whitelabelConfiguration.ProductName }) }), _jsx(Box, { mt: 12, children: _jsx(Form, { onSubmit: handleSubmit }) })] })] }) }));
};
export default Login;
